import React, { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import AudioPlayer from './AudioPlayer';
import MediaModal from './MediaModal';

function getAuthorName(message, isFromAgent) {
  // Agente logado
  if (isFromAgent) return 'Você';

  // Mensagem automática do Viddy
  if (message.external_author_name === 'Viddy')
    return 'Isis - Assistente de Pagamento Viddy';

  // Sistema
  if (message.external_author_name === 'Sistema') return 'Sistema';

  // Cliente (via WhatsApp)
  if (message.external_author_name) return message.external_author_name;

  // Outro agente (transferência)
  if (message.created_by?.name) return message.created_by.name;

  return 'Sistema';
}

function getAuthorColor(message, isFromAgent) {
  if (isFromAgent) return '#085041';
  if (message.external_author_name === 'Viddy') return '#085041';
  if (message.source === 'whatsapp') return '#0C447C';
  return '#5F5E5A';
}

function getTimeString(message) {
  if (!message.created_at) return '';
  try {
    return format(parseISO(message.created_at), "HH':'mm", { locale: ptBR });
  } catch (e) {
    return '';
  }
}

function isAudioAttachment(message) {
  if (!message.attachment) return false;
  const ext = (message.attachment.extension || '').toLowerCase();
  return ['ogg', 'mp3', 'm4a', 'wav', 'aac', 'opus'].includes(ext);
}

function isVideoAttachment(message) {
  if (!message.attachment) return false;
  const ext = (message.attachment.extension || '').toLowerCase();
  return ['mp4', '3gp', 'mov', 'avi', 'webm'].includes(ext);
}

function isImageAttachment(message) {
  if (!message.attachment) return false;
  const ext = (message.attachment.extension || '').toLowerCase();
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
}

function isFileAttachment(message) {
  return (
    message.attachment &&
    !isAudioAttachment(message) &&
    !isImageAttachment(message) &&
    !isVideoAttachment(message)
  );
}

export default function MessageBubble({
  message,
  alignRight,
  isFromAgent,
  isSystem,
}) {
  const [mediaModal, setMediaModal] = useState(null);

  const authorName = useMemo(
    () => getAuthorName(message, isFromAgent),
    [message, isFromAgent]
  );

  const authorColor = useMemo(
    () => getAuthorColor(message, isFromAgent),
    [message, isFromAgent]
  );

  const timeStr = useMemo(() => getTimeString(message), [message]);

  // Mensagem de sistema
  if (isSystem) {
    return (
      <div
        className="d-flex justify-content-center"
        style={{ margin: '8px 0' }}
      >
        <span
          style={{
            fontSize: '11px',
            color: '#5F5E5A',
            background: 'rgba(255,255,255,0.8)',
            padding: '4px 14px',
            borderRadius: '10px',
            textAlign: 'center',
            maxWidth: '80%',
          }}
        >
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <>
      <div
        className={`d-flex mb-2 ${
          alignRight ? 'justify-content-end' : 'justify-content-start'
        }`}
      >
        <div
          style={{
            maxWidth: '75%',
            padding: '8px 12px',
            borderRadius: 8,
            background: alignRight ? '#DCF8C6' : '#fff',
            boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
            fontSize: '13px',
          }}
        >
          {/* Autor */}
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: authorColor,
              marginBottom: '2px',
            }}
          >
            {authorName}
          </div>

          {/* Conteúdo */}
          {isAudioAttachment(message) && (
            <AudioPlayer url={message.attachment.url} />
          )}

          {isImageAttachment(message) && (
            <div
              role="button"
              tabIndex={0}
              onClick={() =>
                setMediaModal({ url: message.attachment.url, type: 'image' })
              }
              style={{ marginBottom: '4px', cursor: 'zoom-in' }}
            >
              <img
                src={message.attachment.url}
                alt="Imagem"
                style={{
                  maxWidth: '100%',
                  maxHeight: '200px',
                  borderRadius: '6px',
                }}
              />
            </div>
          )}

          {isVideoAttachment(message) && (
            <div
              role="button"
              tabIndex={0}
              onClick={() =>
                setMediaModal({ url: message.attachment.url, type: 'video' })
              }
              style={{
                marginBottom: '4px',
                position: 'relative',
                cursor: 'pointer',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '120px',
                  borderRadius: '6px',
                  background: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <i
                  className="las la-play-circle"
                  style={{ fontSize: '40px', color: '#fff' }}
                />
              </div>
              <div
                style={{ fontSize: '11px', color: '#5F5E5A', marginTop: '2px' }}
              >
                {message.attachment.name || 'Vídeo'}
              </div>
            </div>
          )}

          {isFileAttachment(message) && (
            <div style={{ marginBottom: '4px' }}>
              <a
                href={message.attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  color: '#0C447C',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <i className="las la-file-alt" style={{ fontSize: '16px' }} />
                {message.attachment.name || 'Arquivo'}
              </a>
            </div>
          )}

          {message.content && (
            <div style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}>
              {message.content}
            </div>
          )}

          {/* Hora */}
          <div
            style={{
              fontSize: '10px',
              color: '#888780',
              textAlign: 'right',
              marginTop: '2px',
            }}
          >
            {timeStr}
          </div>
        </div>
      </div>

      {mediaModal && (
        <MediaModal
          url={mediaModal.url}
          type={mediaModal.type}
          onClose={() => setMediaModal(null)}
        />
      )}
    </>
  );
}
